/**
 * sl-dpq2.5: Workflow Attachment Chip
 *
 * Shows a compact summary of selected workflow nodes in the AI chat panel.
 */
import { SignalWatcher, WithDisposable } from '@blocksuite/affine/global/lit';
import { ShadowlessElement } from '@blocksuite/affine/std';
import { ToolIcon } from '@blocksuite/icons/lit';
import { html } from 'lit';
import { property } from 'lit/decorators.js';

import type { WorkflowAttachment } from '../ai-chat-content/type';
import { getWorkflowAttachmentLabel } from './workflow-attachment-label';

export class ChatPanelWorkflowChip extends SignalWatcher(
  WithDisposable(ShadowlessElement)
) {
  @property({ attribute: false })
  accessor workflow!: WorkflowAttachment;

  @property({ attribute: false })
  accessor onRemove: ((workflow: WorkflowAttachment) => void) | undefined;

  override render() {
    return html`<chat-panel-chip
      .state=${'finished'}
      .name=${getWorkflowAttachmentLabel(this.workflow)}
      .tooltip=${this._getTooltip()}
      .icon=${ToolIcon()}
      .closeable=${!!this.onRemove}
      .onChipDelete=${this._handleRemove}
    ></chat-panel-chip>`;
  }

  private _getTooltip(): string {
    const {
      workflowId,
      workflowName,
      versionKey,
      access,
      selectedNodes,
      haloNodes,
      edges,
    } = this.workflow;
    const selected = selectedNodes
      .map(n => `  ${n.name} (${n.type})`)
      .join('\n');
    const halo = haloNodes.map(n => `  ${n.name} (${n.type})`).join('\n');
    return [
      `Workflow: ${workflowName ?? workflowId}`,
      versionKey ? `Version: ${versionKey}` : '',
      access ? `Access: ${access}` : '',
      `Selected (${selectedNodes.length}):`,
      selected,
      halo.length > 0 ? `Context (${haloNodes.length}):` : '',
      halo,
      `Edges: ${edges.length}`,
    ]
      .filter(Boolean)
      .join('\n');
  }

  private readonly _handleRemove = () => {
    this.onRemove?.(this.workflow);
  };
}

declare global {
  interface HTMLElementTagNameMap {
    'chat-panel-workflow-chip': ChatPanelWorkflowChip;
  }
}
